import { useCallback, useEffect, type FC, type PropsWithChildren } from 'react';
import { AuthProvider as OriginalOAuthProvider, useAuth as useOidcAuth } from 'react-oidc-context';

import { authProviderConfig } from '#apps/client-web/features/auth/config/oidc';
import { AUTH_UNAUTHORIZED_EVENT } from '#apps/client-web/app/services/react-query-client';

/**
 * Listens for global 401 events dispatched by the MutationCache
 * and redirects the user to the sign-in page.
 */
const UnauthorizedRedirectHandler: FC<PropsWithChildren> = ({ children }) => {
  const { signinRedirect } = useOidcAuth();

  useEffect(() => {
    const handler = () => {
      void signinRedirect().catch((error) => {
        console.error('Failed to redirect to sign-in after an unauthorized event.', error);
      });
    };

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
  }, [signinRedirect]);

  return children;
};

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  // Clear auth params from URL after sign-in to prevent repeated processing
  const signInCallback = useCallback(() => {
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  return (
    <OriginalOAuthProvider
      {...authProviderConfig}
      onSigninCallback={signInCallback}>
      <UnauthorizedRedirectHandler>
        {children}
      </UnauthorizedRedirectHandler>
    </OriginalOAuthProvider>
  );
};
